import type { CalendarEvent } from '../types';
import './EventCard.css';

interface EventCardProps {
  event: CalendarEvent;
  isSaved?: boolean;
  onOpenDetail?: () => void;
}

function formatDate(d: string) {
  return new Date(d).toLocaleDateString('en-AU', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function formatRange(start: string, end: string) {
  const s = new Date(start);
  const e = new Date(end);
  if (s.toDateString() === e.toDateString()) {
    return formatDate(start);
  }
  return `${formatDate(start)} – ${formatDate(end)}`;
}

function getDayNum(d: string) {
  return new Date(d).getDate();
}

function getMonthShort(d: string) {
  return new Date(d).toLocaleDateString('en-AU', { month: 'short' }).toUpperCase();
}

const categoryClass: Record<string, string> = {
  cultural: 'category-cultural',
  sporting: 'category-sporting',
  music: 'category-music',
  industry: 'category-industry',
};

export function EventCard({ event, isSaved, onOpenDetail }: EventCardProps) {
  const slug = (event.category as { slug?: string } | undefined)?.slug ?? '';
  const categoryClassname = categoryClass[slug] ?? '';
  const categoryName = (event.category as { name?: string })?.name ?? 'Event';

  const handleClick = () => onOpenDetail?.();
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onOpenDetail?.();
    }
  };

  return (
    <article
      className={`event-card ${categoryClassname} ${onOpenDetail ? 'event-card--clickable' : ''}`}
      onClick={onOpenDetail ? handleClick : undefined}
      onKeyDown={onOpenDetail ? handleKeyDown : undefined}
      role={onOpenDetail ? 'button' : undefined}
      tabIndex={onOpenDetail ? 0 : undefined}
      aria-label={onOpenDetail ? `View details: ${event.title}` : undefined}
    >
      <div className="event-card-accent" aria-hidden="true" />
      {isSaved && (
        <span className="event-card-saved-badge" aria-hidden="true">Saved</span>
      )}

      <div className="event-card-date-block">
        <span className="event-card-day">{getDayNum(event.start_date)}</span>
        <span className="event-card-month">{getMonthShort(event.start_date)}</span>
      </div>

      <div className="event-card-body">
        <div className="event-card-meta">
          <span className="event-card-category">{categoryName}</span>
          <span className="event-card-dates">
            {formatRange(event.start_date, event.end_date)}
          </span>
        </div>

        <h2 className="event-card-title">{event.title}</h2>

        {event.location && (
          <p className="event-card-location">
            <span className="event-card-location-icon" aria-hidden="true">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                <circle cx="12" cy="10" r="3"/>
              </svg>
            </span>
            {event.location}
          </p>
        )}

        {event.description && (
          <p className="event-card-description">{event.description}</p>
        )}

        {event.host && (event.host as { name?: string }).name && (
          <p className="event-card-host">
            Host: {(event.host as { name: string }).name}
          </p>
        )}

        {event.external_url && (
          <a
            href={event.external_url}
            target="_blank"
            rel="noopener noreferrer"
            className="event-card-link"
            onClick={(e) => e.stopPropagation()}
          >
            Visit event site
            <span className="event-card-link-arrow" aria-hidden="true">→</span>
          </a>
        )}
      </div>
    </article>
  );
}
